import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Trash2, X, Minus, Square, Maximize2, FileX } from 'lucide-react';
import { playSound, SOUNDS } from '../App';

const failedProjects = [
  { name: 'chatbot_v1.exe', reason: 'Died in committee review' },
  { name: 'ml_pilot_q2.py', reason: 'Scope creep to infinity' },
  { name: 'ai_strategy_deck.pptx', reason: 'Never left PowerPoint' },
  { name: 'data_lake_initiative/', reason: '"We need more data" loop' },
  { name: 'vendor_poc_final_v3.zip', reason: 'Vendor disappeared' },
];

export default function RecycleWindow({ 
  zIndex, 
  onClose, 
  onMinimize, 
  onFocus,
  position = { x: 200, y: 100 },
  onPositionChange
}) {
  const [isMaximized, setIsMaximized] = useState(false);

  const handleMaximize = () => {
    playSound(SOUNDS.click);
    setIsMaximized(!isMaximized);
  };

  const windowStyle = isMaximized
    ? { zIndex, left: 0, top: 0, width: '100%', height: 'calc(100vh - 36px)' }
    : { zIndex, left: position.x, top: position.y };

  return (
    <motion.div
      className={`win-window recycle-window-container ${isMaximized ? 'maximized' : ''}`}
      style={windowStyle}
      initial={{ opacity: 0, scale: 0.95, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: 10 }}
      transition={{ duration: 0.15, ease: 'easeOut' }}
      onClick={onFocus}
      drag={!isMaximized}
      dragMomentum={false}
      dragElastic={0}
      dragConstraints={{ left: -500, right: 1400, top: 0, bottom: 700 }}
      onDragEnd={(event, info) => {
        if (!isMaximized) {
          const newX = Math.max(0, position.x + info.offset.x);
          const newY = Math.max(0, position.y + info.offset.y);
          onPositionChange?.({ x: newX, y: newY });
        }
      }}
    >
      <div 
        className="win-window-header"
        onDoubleClick={handleMaximize}
        style={{ cursor: isMaximized ? 'default' : 'grab' }}
      >
        <div className="win-window-title">
          <Trash2 size={14} />
          Recycle Bin - Industry AI Projects
        </div>
        <div className="win-window-controls">
          <button 
            className="win-ctrl-btn" 
            onClick={(e) => { e.stopPropagation(); playSound(SOUNDS.click); onMinimize(); }}
            title="Minimize"
          >
            <Minus size={10} />
          </button>
          <button 
            className="win-ctrl-btn" 
            onClick={(e) => { e.stopPropagation(); handleMaximize(); }}
            title={isMaximized ? "Restore" : "Maximize"}
          >
            {isMaximized ? <Square size={8} /> : <Maximize2 size={10} />}
          </button>
          <button 
            className="win-ctrl-btn win-close" 
            onClick={(e) => { e.stopPropagation(); playSound(SOUNDS.windowClose); onClose(); }}
            title="Close"
          >
            <X size={10} />
          </button>
        </div>
      </div>
      
      <div className="win-window-body">
        <div className="recycle-content">
          <div className="recycle-header">
            <span className="recycle-title">Failed AI Projects</span>
            <span className="recycle-subtitle">(Not ours - we have 0 failed pilots)</span>
          </div>
          
          <div className="recycle-list">
            {failedProjects.map((project, i) => (
              <div key={i} className="recycle-item">
                <FileX size={16} className="recycle-item-icon" />
                <div className="recycle-item-info">
                  <span className="recycle-item-name">{project.name}</span>
                  <span className="recycle-item-reason">Deleted: {project.reason}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="recycle-footer">
            <p>🎯 <strong>Our approach:</strong> Ship fast, stay reliable, own the outcome.</p>
            <p>That's why our recycle bin stays empty.</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
